import React, { useMemo } from "react";
import { Updater } from "use-immer";

import {
  IgAutocomplete,
  IgTextField,
  IgTypography,
  IgTypographyTooltip,
} from "@intelligems/ig-theme";
import {
  OfferProductDto,
  QuantityButtonProductVariant,
} from "@intelligems/ig-types";
import { Grid, MenuItem } from "@mui/material";

const createGroupedVariants = (offerProducts: OfferProductDto[]) => {
  const groupedVariants: QuantityButtonProductVariant[] = [];

  offerProducts.forEach((product) => {
    product.variants.forEach((variant) => {
      groupedVariants.push({
        ...variant,
        productId: product.id,
        productTitle: product.title,
      });
    });
  });

  return groupedVariants;
};

interface QuantityButtonProductDropdownProps {
  offerProducts: OfferProductDto[];
  setFocusedOfferVariant: Updater<QuantityButtonProductVariant>;
  focusedOfferVariant: QuantityButtonProductVariant;
}

export const QuantityButtonProductDropdown = ({
  offerProducts,
  setFocusedOfferVariant,
  focusedOfferVariant,
}: QuantityButtonProductDropdownProps) => {
  const groupedOptions = useMemo(() => {
    return createGroupedVariants(offerProducts);
  }, [offerProducts]);

  return (
    <IgAutocomplete
      value={focusedOfferVariant}
      componentsProps={{
        paper: {
          elevation: 0,
          sx: {
            border: "1px solid rgba(0,0,0, .12)",
            width: "100%",
            minWidth: "210px",
            borderRadius: "5px",
          },
        },
      }}
      isOptionEqualToValue={(option, value) => {
        return option.id === value.id;
      }}
      groupBy={(option) => option.productTitle}
      onChange={(_ev, newValue) => {
        setFocusedOfferVariant(newValue);
      }}
      renderGroup={(params) => {
        return (
          <li key={params.key}>
            <Grid
              item
              container
              direction={"row"}
              alignItems={"center"}
              sx={{ backgroundColor: "#F5F5F5", height: "40px" }}
            >
              <Grid item sx={{ marginLeft: "15px" }}>
                <IgTypography igStyle={"semibold"}>{params.group}</IgTypography>
              </Grid>
            </Grid>
            <div>{params.children}</div>
          </li>
        );
      }}
      getOptionLabel={(option) => {
        return `${option.title}`;
      }}
      id="quantity-button-product-dropdown"
      options={groupedOptions}
      disableClearable={true}
      renderInput={(params) => {
        return (
          <IgTextField sx={{ width: "200px" }} {...params} size={"small"} />
        );
      }}
      filterOptions={(options, state) => {
        return options.filter((option) => {
          const matchesVariantName = option.title
            .toLowerCase()
            .includes(state.inputValue.toLowerCase());
          const matchesProductName = option.productTitle
            .toLowerCase()
            .includes(state.inputValue.toLowerCase());

          return matchesProductName || matchesVariantName;
        });
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        return (
          <MenuItem
            key={`${key}-${props.id}`}
            {...optionProps}
            sx={{ height: "32px", paddingLeft: 1 }}
          >
            <IgTypographyTooltip
              textMaxWidth={"200px"}
              textSx={{ fontWeight: 400 }}
              variant={"body1"}
              differentTooltipText={`${option.productTitle} - ${props.key}`}
              text={props.key}
            />
          </MenuItem>
        );
      }}
    />
  );
};
